import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import {
  FeedListItem,
  FeedListInfinitySpinner,
  LoaderDashboard,
  DashboardHeading,
  Breadcrumbs,
  EmptyFeed,
} from 'components';
import { useFeatureFlagState } from 'context';
import { SearchFeedQuery, handleFetchMoreError, criticalFeedError } from 'services/apollo';
import { useInView } from 'react-intersection-observer';
import styles from './SearchFeed.module.scss';

interface searchFeedProps {
  searchString: string;
}

export const SearchFeed: React.FC<searchFeedProps> = ({ searchString }) => {
  const { groupsFeatureFlag } = useFeatureFlagState();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { error, data, fetchMore } = useQuery(SearchFeedQuery, {
    variables: { cursor: '', searchString, includeGroups: groupsFeatureFlag },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const [ref, inView] = useInView({
    rootMargin: '500px 0px',
    threshold: 0,
  });

  const triggerFetchMore = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.me.search.pageInfo;
    try {
      await fetchMore({
        variables: { cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.me.search.nodes = [
            ...prevResult.me.search.nodes,
            ...fetchMoreResult.me.search.nodes,
          ];
          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  useEffect(() => {
    if (inView === true) {
      triggerFetchMore().then((response) => response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (data === undefined) return <LoaderDashboard layout="SearchFeed" />;

  if (error && criticalFeedError(error)) return <LoaderDashboard layout="SearchFeed" />;

  if (data.me.search.searchTerm !== searchString) {
    return <LoaderDashboard layout="SearchFeed" />;
  }

  return (
    <>
      <Breadcrumbs
        links={[
          {
            label: searchString,
            to: `/search/qs?term=${encodeURIComponent(searchString)}`,
            reload: true,
          },
        ]}
      />
      <div className={styles.Heading}>
        <DashboardHeading level={1}>
          {data?.me?.search?.pageInfo?.total} results for “{searchString}”
        </DashboardHeading>
      </div>
      {data.me.search.nodes.map((node: any, key: number) => (
        <FeedListItem
          nodeId={node.id}
          nodeType={node.__typename}
          key={key}
          name={node.title}
          baseUrl={node.baseUrl}
          url={node.url}
          owner={node.owners[0]}
          collaborators={node.accessorInfo}
          modified={node.modified}
          created={node.created}
          nodeIcon={node.category}
          isIntegrationConnected={node.isIntegrationConnected}
          integrationType={node.integrationType}
          trackIndex={key}
          trackFeedType={'SearchFeed'}
          trackTotalItems={data.me.search.pageInfo.total}
          trackSearchTerm={searchString}
          isShareable={node.sharingEnabled}
          shareTargets={node.shareTargets}
        />
      ))}
      {data?.me?.search?.pageInfo?.hasNextPage && (
        <div ref={ref}>
          <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
        </div>
      )}
      {!data.me.search.nodes.length && <EmptyFeed message={'Add documents to see app activity'} />}
    </>
  );
};
